/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-refresh/only-export-components */
// SocketContext.tsx
import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import io, { Socket } from "socket.io-client";
import { Subject } from "rxjs";
import {
  SocketEvent,
  EventPayloads,
  EventResponses,
  SocketType,
  AdminStatus,
} from "@common/SocketEvents";
import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Collapse,
  Link,
} from "@mui/material";
import { useAppContext } from "./App.context";
import { setProgressSample } from "./PlayerProgress.store";

const RETRY_DELAY_MS = 10_000;

// Everything in a playlist update except the fields that change on every
// player tick (progress and the timestamp of that tick).
const playlistSignature = (
  message: EventResponses[SocketEvent.PLAYLIST_UPDATE]
) =>
  JSON.stringify({
    ...message,
    lastPlayerUpdate: undefined,
    playerMeta: { ...message.playerMeta, currentProgress: undefined },
  });

interface ISocketContext {
  socket: Socket;
  sendMessage: (message: EventPayloads[SocketEvent.MESSAGE]) => void;
  sendRequest: <T extends keyof EventPayloads>(
    event: T,
    data: EventPayloads[T]
  ) => Promise<EventResponses[T]>;
  messageSubject: Subject<EventPayloads[SocketEvent.MESSAGE]>;
  roomId: string;
  // null until the server sends it; the Nest server never does (no admin PIN).
  adminStatus: AdminStatus | null;
}

const SocketContext = createContext<ISocketContext>({} as ISocketContext);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider: React.FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const {
    data: { roomId },
  } = useAppContext();

  // const getRoomIdFromURL = () => {
  //   // const queryParams = new URLSearchParams(window.location.search);
  //   // return queryParams.get("roomId") || "defaultRoom"; // Fallback to 'defaultRoom' if roomId is not provided
  //   return data.roomId;
  // };

  // const roomId = getRoomIdFromURL();

  const socket: Socket = useMemo(
    () =>
      io(import.meta.env.VITE_SERVER_URL, {
        query: { roomId: roomId, mode: SocketType.CLIENT },
      }),
    []
  );
  // Refs, not state: these change on every player tick and must not re-render.
  const lastPlaylistUpdate = useRef<number>(Date.now());
  const lastSignature = useRef<string>("");
  const lastPlayerReportAt = useRef<string>("");
  const serverTracksPlayer = useRef(false);
  const lastVideoId = useRef<string | undefined>(undefined);
  const messageSubject = new Subject<EventPayloads[SocketEvent.MESSAGE]>();
  const [isConnected, setConnection] = useState(false);
  const [connectionError, setConnectionError] = useState("");
  const [retrying, setRetrying] = useState(false);
  const [adminStatus, setAdminStatus] = useState<AdminStatus | null>(null);
  // Read inside socket listeners, which are registered once.
  const { data: appData } = useAppContext();
  const rememberedPin = useRef<string | undefined>(undefined);
  rememberedPin.current = appData.adminPins?.[roomId];
  const autoLoginTried = useRef(false);
  const administratorOverride = useRef(false);
  administratorOverride.current = !!appData.administratorOverride;
  const [showErrorDetail, setShowErrorDetail] = useState(false);
  const { setData } = useAppContext();

  useEffect(() => {
    // Event listener for 'message' event
    socket.on(
      SocketEvent.MESSAGE,
      (message: EventResponses[SocketEvent.MESSAGE]) => {
        messageSubject.next(message);
      }
    );
    socket.on(SocketEvent.CONNECT, () => {
      autoLoginTried.current = false;
      setConnection(true);
      setConnectionError(""); // Reset connection error on successful connect
      setRetrying(false);
    });

    socket.on(SocketEvent.DISCONNECT, () => {
      setConnection(false);
    });

    socket.on(SocketEvent.CONNECT_ERROR, (error) => {
      socket.disconnect();
      setConnection(false);
      setConnectionError(String(error));
      setRetrying(false);
    });

    socket.on(SocketEvent.ERROR, (error) => {
      socket.disconnect();
      setConnection(false);
      setConnectionError(String(error));
      setRetrying(false);
    });

    socket.on(SocketEvent.ADMIN_STATUS, (status: AdminStatus) => {
      setAdminStatus(status);

      // "Stay admin on this device": unlock with the remembered PIN once per
      // connection. If it no longer works (PIN changed), forget it.
      // Room without a PIN, Administrator Override on: unlock with an empty
      // PIN so the guest permission switches don't apply to this device.
      if (
        !status.hasAdminPassword &&
        administratorOverride.current &&
        status.exemptFromRestrictions === false &&
        !autoLoginTried.current
      ) {
        autoLoginTried.current = true;
        socket.emit(SocketEvent.ADMIN_LOGIN, { password: "" }, () => {});
        return;
      }
      const pin = rememberedPin.current;
      if (status.hasAdminPassword && !status.isAdmin && pin && !autoLoginTried.current) {
        autoLoginTried.current = true;
        socket.emit(
          SocketEvent.ADMIN_LOGIN,
          { password: pin },
          (result: EventResponses[SocketEvent.ADMIN_LOGIN]) => {
            if (!result?.ok) {
              setData("adminPins", (pins) => {
                const next = { ...pins };
                delete next[roomId];
                return next;
              });
            }
          }
        );
      }
    });

    socket.on(
      SocketEvent.PLAYLIST_UPDATE,
      (message: EventResponses[SocketEvent.PLAYLIST_UPDATE]) => {
        lastPlaylistUpdate.current = Date.now();

        // Only the player's own reports bump lastPlayerUpdate. Other broadcasts
        // carry the server's intent, e.g. "play B from 0" right after a skip,
        // while the player is still loading B (or showing an ad).
        const playerReportAt = String(message.lastPlayerUpdate);
        const videoId = message.playerMeta.currentVideo?.id;
        // Desktop offline (Go server tells us): nothing is playing, whatever
        // the last report said, so hold the bar.
        const playerOffline = message.playerConnected === false;
        if (playerOffline) {
          setProgressSample(message.playerMeta.currentProgress, false);
        } else if (playerReportAt !== lastPlayerReportAt.current) {
          lastPlayerReportAt.current = playerReportAt;
          // During an ad the song itself isn't advancing.
          setProgressSample(
            message.playerMeta.currentProgress,
            message.playerMeta.isPlaying && !message.playerMeta.isAdPlaying
          );
        } else if (videoId !== lastVideoId.current) {
          // Switched videos: hold at the start until the player reports.
          setProgressSample(message.playerMeta.currentProgress, false);
        }
        lastVideoId.current = videoId;
        if (typeof message.playerConnected === "boolean") {
          // The server knows: it sees the desktop connect and disconnect.
          serverTracksPlayer.current = true;
          setData("isPlayerConnected", message.playerConnected);
        } else {
          setData("isPlayerConnected", true);
        }

        // The player reports every ~500ms and usually only the progress has
        // moved. Progress lives in its own store, so skip the app-wide update
        // (and re-render) unless something else changed.
        const signature = playlistSignature(message);
        if (signature !== lastSignature.current) {
          lastSignature.current = signature;
          setData("playlistData", message);
        }
      }
    );

    // Cleanup on unmount
    return () => {
      socket.off(SocketEvent.MESSAGE);
      socket.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = Date.now();
      const diff = now - lastPlaylistUpdate.current;

      // Only a guess for servers that don't report playerConnected (Nest).
      if (!serverTracksPlayer.current && diff > 20_000) {
        // If more than 20 seconds have passed, player is not connected
        setData("isPlayerConnected", false);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Function to send messages
  const sendMessage = (message: EventPayloads[SocketEvent.MESSAGE]) => {
    socket.emit(SocketEvent.MESSAGE, message);
  };

  // Function to send requests and receive a promise-based response
  const sendRequest = <T extends keyof EventPayloads>(
    event: T,
    data: EventPayloads[T]
  ): Promise<EventResponses[T]> => {
    return new Promise((resolve) => {
      socket.emit(event, data, (response: EventResponses[T]) => {
        return resolve(response);
      });
    });
  };

  // Keep the error on screen while retrying (the button spins) instead of
  // flipping back to the full-screen spinner on every attempt.
  const retryConnection = () => {
    setRetrying(true);
    socket.disconnect();
    socket.connect(); // Attempt to reconnect
  };

  // Retry automatically 10s after each failed attempt.
  useEffect(() => {
    if (!connectionError || retrying) return;
    const timer = setTimeout(retryConnection, RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [connectionError, retrying]);

  // Expose the socket, sendMessage function, sendRequest, and messageSubject
  return (
    <SocketContext.Provider
      value={{
        socket,
        sendMessage,
        sendRequest,
        messageSubject,
        roomId,
        adminStatus,
      }}
    >
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={!isConnected}
      >
        {!connectionError ? (
          <CircularProgress color="inherit" />
        ) : (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={retryConnection}
                disabled={retrying}
                startIcon={
                  retrying && <CircularProgress color="inherit" size={14} />
                }
              >
                {retrying ? "Retrying" : "Retry Now"}
              </Button>
            }
          >
            Connection failed.{" "}
            {retrying ? "Retrying…" : "Will retry in 10 seconds."}
            <br />
            <Link
              component="button"
              color="inherit"
              variant="body2"
              onClick={() => setShowErrorDetail((show) => !show)}
            >
              {showErrorDetail ? "Hide details" : "Details"}
            </Link>
            <Collapse in={showErrorDetail}>
              <small>{connectionError}</small>
            </Collapse>
          </Alert>
        )}
      </Backdrop>
      {children}
    </SocketContext.Provider>
  );
};
